import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "../i18n";
import type { PlatformUsageStats } from "../types/admin";
import { adminApi } from "../services/adminApi";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";

export const PlatformStatsCard = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState<PlatformUsageStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<"24h" | "7d" | "30d">("30d");
  const [metric, setMetric] = useState<"cost" | "tokens">("cost");

  const fetchStats = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await adminApi.getPlatformStats(timeRange);
      setStats(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load stats");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    // Auto-refresh every 60 seconds
    const interval = setInterval(fetchStats, 60000);
    return () => clearInterval(interval);
  }, [timeRange]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat("en-US").format(value);
  };

  const chartData = useMemo(() => {
    if (!stats?.daily_breakdown) return null;
    
    const values = stats.daily_breakdown.map((d) => 
      metric === "tokens" ? d.tokens : d.cost_usd
    );
    const max = Math.max(...values, 1);
    const width = 600;
    const height = 160;
    
    const points = stats.daily_breakdown
      .map((d, idx) => {
        const x = (idx / (stats.daily_breakdown.length - 1 || 1)) * width;
        const value = metric === "tokens" ? d.tokens : d.cost_usd;
        const y = height - (value / max) * (height - 20) - 10;
        return `${x.toFixed(2)},${y.toFixed(2)}`;
      })
      .join(" ");
    
    const area = `0,160 ${points} 600,160`;
    
    return { points, area };
  }, [stats, metric]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">
            {t("admin.platform_stats", "Platform Usage Overview")}
          </CardTitle>
          <div className="flex items-center gap-2">
            <div className="flex gap-1 border rounded-md p-1">
              <Button
                size="sm"
                variant={timeRange === "24h" ? "default" : "ghost"}
                onClick={() => setTimeRange("24h")}
              >
                {t("admin.24h", "24h")}
              </Button>
              <Button
                size="sm"
                variant={timeRange === "7d" ? "default" : "ghost"}
                onClick={() => setTimeRange("7d")}
              >
                {t("admin.7d", "7d")}
              </Button>
              <Button
                size="sm"
                variant={timeRange === "30d" ? "default" : "ghost"}
                onClick={() => setTimeRange("30d")}
              >
                {t("admin.30d", "30d")}
              </Button>
            </div>
            <Button variant="outline" size="sm" onClick={fetchStats}>
              {t("admin.refresh", "Refresh")}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading && (
          <div className="text-sm text-muted-foreground">
            {t("admin.loading", "Loading...")}
          </div>
        )}

        {error && (
          <div className="text-sm text-destructive">{error}</div>
        )}

        {!isLoading && stats && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-1">
                <div className="text-xs text-muted-foreground">
                  {t("admin.total_cost", "Total Cost")}
                </div>
                <div className="text-2xl font-semibold">
                  {formatCurrency(stats.total_cost_usd)}
                </div>
              </div>
              <div className="space-y-1">
                <div className="text-xs text-muted-foreground">
                  {t("admin.active_users_count", "Active Users")}
                </div>
                <div className="text-2xl font-semibold">
                  {formatNumber(stats.active_users)}
                </div>
              </div>
              <div className="space-y-1">
                <div className="text-xs text-muted-foreground">
                  {t("admin.total_events", "Total Events")}
                </div>
                <div className="text-2xl font-semibold">
                  {formatNumber(stats.total_events)}
                </div>
              </div>
              <div className="space-y-1">
                <div className="text-xs text-muted-foreground">
                  {t("admin.total_tokens", "Total Tokens")}
                </div>
                <div className="text-2xl font-semibold">
                  {formatNumber(stats.total_tokens)}
                </div>
              </div>
            </div>

            {stats.daily_breakdown && stats.daily_breakdown.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium">
                      {t("admin.usage_over_time", "Usage over time")}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {t("admin.daily_totals", "Daily totals")}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant={metric === "cost" ? "default" : "outline"}
                      onClick={() => setMetric("cost")}
                    >
                      {t("admin.metric_cost", "Cost")}
                    </Button>
                    <Button
                      size="sm"
                      variant={metric === "tokens" ? "default" : "outline"}
                      onClick={() => setMetric("tokens")}
                    >
                      {t("admin.metric_tokens", "Tokens")}
                    </Button>
                  </div>
                </div>
                {chartData && (
                  <div className="rounded-lg border bg-background/60 p-3">
                    <svg viewBox="0 0 600 160" className="w-full h-40" role="img">
                      <defs>
                        <linearGradient id="platform-usageArea" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.4" />
                          <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0" />
                        </linearGradient>
                      </defs>
                      <polygon points={chartData.area} fill="url(#platform-usageArea)" />
                      <polyline
                        points={chartData.points}
                        fill="none"
                        stroke="hsl(var(--primary))"
                        strokeWidth="3"
                      />
                    </svg>
                  </div>
                )}
              </div>
            )}

            {stats.top_users.length > 0 && (
              <div className="space-y-2">
                <div className="text-sm font-medium">
                  {t("admin.top_users_by_cost", "Top Users by Cost")}
                </div>
                <div className="border rounded-md overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium">
                          {t("admin.user", "User")}
                        </th>
                        <th className="text-right px-3 py-2 font-medium">
                          {t("admin.cost", "Cost")}
                        </th>
                        <th className="text-right px-3 py-2 font-medium">
                          {t("admin.tokens", "Tokens")}
                        </th>
                        <th className="text-right px-3 py-2 font-medium">
                          {t("admin.events", "Events")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.top_users.map((user, idx) => (
                        <tr
                          key={user.user_id}
                          className={idx % 2 === 0 ? "bg-background" : "bg-muted/30"}
                        >
                          <td className="px-3 py-2">
                            <div className="font-mono text-xs truncate max-w-[200px]">
                              {user.user_id}
                            </div>
                          </td>
                          <td className="text-right px-3 py-2 font-medium">
                            {formatCurrency(user.cost_usd)}
                          </td>
                          <td className="text-right px-3 py-2">
                            {formatNumber(user.tokens)}
                          </td>
                          <td className="text-right px-3 py-2">
                            {formatNumber(user.events)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default PlatformStatsCard;
